import { Box, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useMemo, useState } from "react";

import { monospaceStyle } from "../../../theme";
import { PartnershipNode, PartnershipPair, Partnerships } from "../../../types/analytics";
import { counted } from "../Ecosystem/EcosystemParts";
import { clusterOf, tieId } from "./partnershipsLayout";
import { PartnershipsSelection } from "./PartnershipsPanel";
import { clusterColour, groupName, strengthText } from "./PartnershipsParts";

// What a cell of the matrix is shaded by: the strength of the pair's tie,
// or the projects the two share
export type MatrixMeasure = "strength" | "projects";

// The width the row names take, and the height the column names take
const ROW_LABEL_WIDTH = 170;
const COLUMN_LABEL_HEIGHT = 120;
// The side of a cell: as large as the room allows for the groups drawn,
// within these
const CELL_MOST = 22;
const CELL_LEAST = 12;
const CELL_ROOM = 620;
// Cells carry their number while they are at least this large
const CELL_NUMBERED = 18;
// The least shade of a filled cell, so that the weakest still shows
const SHADE_LEAST = 0.18;
// An empty cell, and the diagonal: this much of the overlay ink
const EMPTY_CELL = 0.035;

export const cellSize = (count: number): number =>
  Math.max(CELL_LEAST, Math.min(CELL_MOST, Math.floor(CELL_ROOM / Math.max(1, count))));

export const measureOf = (pair: PartnershipPair, measure: MatrixMeasure): number =>
  measure === "strength" ? pair.strength : pair.projects;

const measureText = (value: number, measure: MatrixMeasure): string =>
  measure === "strength" ? strengthText(value) : String(value);

// The runs of the diagonal that are one cluster each: where each starts
// along the groups and how many it holds. The groups come cluster by
// cluster, so a cluster is one run.
export const clusterBlocks = (
  data: Partnerships,
): { cluster: number; start: number; length: number }[] => {
  const blocks: { cluster: number; start: number; length: number }[] = [];
  data.nodes.forEach((node, index) => {
    const cluster = clusterOf(data, node);
    const last = blocks[blocks.length - 1];
    if (cluster === null) {
      return;
    }
    if (last && last.cluster === cluster && last.start + last.length === index) {
      last.length += 1;
    } else {
      blocks.push({ cluster, start: index, length: 1 });
    }
  });
  return blocks;
};

interface MatrixBodyProps {
  nodes: PartnershipNode[];
  byPair: Map<string, PartnershipPair>;
  measure: MatrixMeasure;
  most: number;
  size: number;
  selectedTie: string | null;
  selectedGroup: string | null;
  onSelect: (selection: PartnershipsSelection) => void;
  onPointColumn: (index: number | null) => void;
}

// The rows of the matrix: each group's name, then a cell per group. Kept
// apart from the column names, so that the pointer moving over the cells
// draws only the names again.
const MatrixBody = React.memo(function MatrixBody({
  nodes,
  byPair,
  measure,
  most,
  size,
  selectedTie,
  selectedGroup,
  onSelect,
  onPointColumn,
}: MatrixBodyProps) {
    const theme = useTheme();
    const accent = theme.palette.accent.main;
    const { chart } = theme.palette;
    const numbered = size >= CELL_NUMBERED;
    return (
      <Box role="rowgroup" onMouseLeave={() => onPointColumn(null)}>
        {nodes.map((row, rowIndex) => (
          <Box
            role="row"
            key={row.id}
            sx={{
              display: "flex",
              alignItems: "center",
              height: size,
              "&:hover .pt-row-name": { color: "text.primary" },
            }}
          >
            <Box
              component="button"
              type="button"
              role="rowheader"
              className="pt-row-name"
              aria-label={groupName(row)}
              aria-pressed={row.id === selectedGroup}
              title={groupName(row)}
              onClick={() => onSelect({ kind: "group", id: row.id })}
              sx={{
                all: "unset",
                boxSizing: "border-box",
                width: ROW_LABEL_WIDTH,
                flexShrink: 0,
                pr: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 0.75,
                fontSize: 11,
                lineHeight: `${size}px`,
                color: row.id === selectedGroup ? "text.primary" : "text.secondary",
                fontWeight: row.id === selectedGroup ? 600 : 400,
                cursor: "pointer",
                whiteSpace: "nowrap",
                "&:focus-visible": { outline: "2px solid", outlineColor: "link.main" },
              }}
            >
              <Box
                component="span"
                sx={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
              >
                {groupName(row)}
              </Box>
              <Box
                component="span"
                aria-hidden
                sx={{
                  flexShrink: 0,
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: clusterColour(row.cluster, chart),
                }}
              />
            </Box>
            {nodes.map((column, columnIndex) => {
              const pair =
                row.id === column.id
                  ? undefined
                  : byPair.get(tieId(row.id < column.id
                      ? { a: row.id, b: column.id }
                      : { a: column.id, b: row.id }));
              const value = pair ? measureOf(pair, measure) : 0;
              const cell = {
                boxSizing: "border-box",
                width: size - 1,
                height: size - 1,
                mr: "1px",
                flexShrink: 0,
              } as const;
              if (row.id === column.id) {
                return (
                  <Box
                    key={column.id}
                    aria-hidden
                    data-testid="pt-diagonal"
                    onMouseEnter={() => onPointColumn(columnIndex)}
                    sx={{
                      ...cell,
                      backgroundColor: alpha(clusterColour(row.cluster, chart), 0.22),
                    }}
                  />
                );
              }
              if (!pair || value <= 0) {
                return (
                  <Box
                    key={column.id}
                    aria-hidden
                    onMouseEnter={() => onPointColumn(columnIndex)}
                    sx={{ ...cell, backgroundColor: alpha(chart.ink, EMPTY_CELL) }}
                  />
                );
              }
              const selected = tieId(pair) === selectedTie;
              const shade = SHADE_LEAST + (1 - SHADE_LEAST) * (most > 0 ? value / most : 0);
              const label = `${groupName(row)} × ${groupName(column)}: strength ${strengthText(
                pair.strength,
              )}, ${counted(pair.projects, "shared project")}`;
              return (
                <Box
                  key={column.id}
                  component="button"
                  type="button"
                  role="gridcell"
                  aria-label={label}
                  aria-selected={selected}
                  title={label}
                  data-testid="pt-cell"
                  data-row={rowIndex}
                  data-column={columnIndex}
                  data-shade={shade.toFixed(2)}
                  onMouseEnter={() => onPointColumn(columnIndex)}
                  onClick={() => onSelect({ kind: "pair", a: pair.a, b: pair.b })}
                  sx={{
                    all: "unset",
                    boxSizing: "border-box",
                    width: size - 1,
                    height: size - 1,
                    mr: "1px",
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    backgroundColor: alpha(accent, shade),
                    outline: selected ? `2px solid ${theme.palette.text.primary}` : undefined,
                    outlineOffset: -1,
                    zIndex: selected ? 1 : undefined,
                    position: "relative",
                    ...monospaceStyle,
                    fontSize: 9,
                    color: shade > 0.55 ? chart.labelOnFill : "text.primary",
                    "&:hover": { outline: `1px solid ${alpha(theme.palette.text.primary, 0.7)}` },
                    "&:focus-visible": { outline: "2px solid", outlineColor: "link.main" },
                  }}
                >
                  {numbered ? measureText(value, measure) : null}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    );
});

interface PartnershipsMatrixProps {
  data: Partnerships;
  measure: MatrixMeasure;
  selection: PartnershipsSelection;
  onSelect: (selection: PartnershipsSelection) => void;
}

// The matrix of the pairs: a row and a column per group in the order of the
// response, cluster by cluster, so that a cluster that works together often
// reads as a bright square on the diagonal, outlined in its colour. A cell
// is shaded in the accent by the pair's strength or its shared projects;
// a cell selects the pair, a name the group.
const PartnershipsMatrix: React.FC<PartnershipsMatrixProps> = ({
  data,
  measure,
  selection,
  onSelect,
}) => {
  const { chart } = useTheme().palette;
  const size = cellSize(data.nodes.length);
  const [column, setColumn] = useState<number | null>(null);
  const byPair = useMemo(
    () => new Map(data.pairs.map((pair) => [tieId(pair), pair])),
    [data],
  );
  const most = useMemo(
    () => Math.max(0, ...data.pairs.map((pair) => measureOf(pair, measure))),
    [data, measure],
  );
  const blocks = useMemo(() => clusterBlocks(data), [data]);
  const selectedTie = selection?.kind === "pair" ? tieId(selection) : null;
  const selectedGroup = selection?.kind === "group" ? selection.id : null;
  return (
    <Box
      role="grid"
      aria-label="Pairs of groups"
      aria-rowcount={data.nodes.length}
      sx={{ position: "relative", width: "max-content" }}
    >
      <Box aria-hidden sx={{ display: "flex", height: COLUMN_LABEL_HEIGHT, pl: `${ROW_LABEL_WIDTH}px` }}>
        {data.nodes.map((node, index) => (
          <Box
            key={node.id}
            data-testid="pt-column-name"
            title={groupName(node)}
            sx={{
              width: size,
              flexShrink: 0,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              pb: 0.75,
              boxSizing: "border-box",
            }}
          >
            <Box
              component="span"
              sx={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxHeight: COLUMN_LABEL_HEIGHT - 10,
                fontSize: 11,
                lineHeight: `${size}px`,
                color:
                  index === column || node.id === selectedGroup
                    ? "text.primary"
                    : "text.secondary",
                fontWeight: node.id === selectedGroup ? 600 : 400,
              }}
            >
              {groupName(node)}
            </Box>
          </Box>
        ))}
      </Box>
      <MatrixBody
        nodes={data.nodes}
        byPair={byPair}
        measure={measure}
        most={most}
        size={size}
        selectedTie={selectedTie}
        selectedGroup={selectedGroup}
        onSelect={onSelect}
        onPointColumn={setColumn}
      />
      {blocks.map((block) => (
        <Box
          key={`${block.cluster}-${block.start}`}
          aria-hidden
          data-testid="pt-block"
          data-cluster={block.cluster}
          data-colour={clusterColour(block.cluster, chart)}
          sx={{
            position: "absolute",
            left: ROW_LABEL_WIDTH + block.start * size - 1,
            top: COLUMN_LABEL_HEIGHT + block.start * size - 1,
            width: block.length * size + 1,
            height: block.length * size + 1,
            boxSizing: "border-box",
            border: `1.5px solid ${clusterColour(block.cluster, chart)}`,
            borderRadius: "2px",
            pointerEvents: "none",
          }}
        />
      ))}
    </Box>
  );
};

// The shades of the matrix, in its legend: from the faintest to the
// fullest, with the weakest and the strongest value of the measure
export const MatrixScale: React.FC<{ data: Partnerships; measure: MatrixMeasure }> = ({
  data,
  measure,
}) => {
  const theme = useTheme();
  const values = data.pairs.map((pair) => measureOf(pair, measure)).filter((value) => value > 0);
  const least = values.length > 0 ? Math.min(...values) : 0;
  const most = values.length > 0 ? Math.max(...values) : 0;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      {measure === "strength" ? "cell = strength of the tie" : "cell = shared projects"}
      <Box component="span" sx={monospaceStyle}>
        {measureText(least, measure)}
      </Box>
      {[SHADE_LEAST, (SHADE_LEAST + 1) / 2, 1].map((shade) => (
        <Box
          key={shade}
          component="span"
          aria-hidden
          sx={{ width: 16, height: 11, backgroundColor: alpha(theme.palette.accent.main, shade) }}
        />
      ))}
      <Box component="span" sx={monospaceStyle}>
        {measureText(most, measure)}
      </Box>
    </Box>
  );
};

export default PartnershipsMatrix;
